// Question comments (review threads, 2026-10-30). After an inspection is filled in, a reviewer comments on any
// question, the department replies under it (text + any file, optionally changing its answer), the reviewer closes it.
// Template setting "Review flow": 0 Findings and CAPA (default, no threads), 1 Question comments, 2 Both.
// Pure helpers only (status text, counts, filter, payload builders, contract normalisers) - unit tested.
import { isImageAttachment } from './ticket-attachment'

export const REVIEW_FLOW = { Findings: 0, Comments: 1, Both: 2 }

export const REVIEW_FLOW_CHOICES = [
    {
        value: REVIEW_FLOW.Findings,
        title: 'Findings and CAPA',
        text: 'Failing answers raise tickets (corrective actions). No question comments.',
    },
    {
        value: REVIEW_FLOW.Comments,
        title: 'Question comments',
        text: 'Reviewers comment on any question; the department replies and attaches proof.',
    },
    {
        value: REVIEW_FLOW.Both,
        title: 'Both',
        text: 'Findings and CAPA for failing answers, plus question comments for the review.',
    },
]

export const THREAD_STATUS = { Open: 1, Replied: 2, Closed: 3 }

export const MESSAGE_KIND = {
    Comment: 1,
    Reply: 2,
    AnswerChanged: 3,
    Closed: 4,
    Reopened: 5,
}

export const MAX_THREAD_FILES = 10
export const MAX_THREAD_TEXT = 4000
export const NOT_ALLOWED_MESSAGE = "You can't do this on this inspection."

const STATUS_INFO = {
    [THREAD_STATUS.Open]: { key: 'open', text: 'Open', hint: 'Waiting for a reply', color: 'red' },
    [THREAD_STATUS.Replied]: { key: 'replied', text: 'Replied', hint: 'Waiting for the reviewer', color: 'orange' },
    [THREAD_STATUS.Closed]: { key: 'closed', text: 'Closed', hint: 'Closed', color: 'green' },
}

const pick = (obj, ...keys) => {
    if (!obj || typeof obj !== 'object') return undefined
    const found = keys.find(k => obj[k] !== undefined && obj[k] !== null)
    return found === undefined ? undefined : obj[found]
}

const toInt = (value, fallback = 0) => {
    const n = Number(value)
    return Number.isFinite(n) ? Math.trunc(n) : fallback
}

const toBool = value => value === true || value === 1 || String(value).toLowerCase() === 'true'

const toText = value => (value == null ? '' : String(value))

export const statusInfo = status =>
    STATUS_INFO[toInt(status)] || { key: 'unknown', text: 'Unknown', hint: '', color: 'grey' }

export const isReviewFlowOn = flow => {
    const n = toInt(flow, 0)
    return n === REVIEW_FLOW.Comments || n === REVIEW_FLOW.Both
}

/** Server dates are UTC; values without a zone get a Z so the browser does not read them as local time. */
export const parseUtc = value => {
    if (!value) return null
    const text = String(value).trim()
    if (!text) return null
    const zoned = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`
    const date = new Date(zoned)
    return Number.isNaN(date.getTime()) ? null : date
}

export const formatThreadTime = value => {
    const date = parseUtc(value)
    if (!date) return ''
    return date.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const stripHtml = html =>
    toText(html)
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, ' ')
        .trim()

export const normalizeFile = file => {
    const out = {
        FileId: toInt(pick(file, 'FileId', 'fileId', 'MediaId'), 0),
        FileName: toText(pick(file, 'FileName', 'fileName', 'Name')),
        FileType: toText(pick(file, 'FileType', 'fileType', 'ContentType')),
        Url: toText(pick(file, 'Url', 'url', 'SignedURL', 'FileURL')),
    }
    const serverIsImage = pick(file, 'IsImage', 'isImage')
    out.IsImage =
        serverIsImage !== undefined
            ? toBool(serverIsImage)
            : isImageAttachment({ FileType: out.FileType, FileName: out.FileName, url: out.Url })
    return out
}

export const normalizeMessage = message => ({
    MessageId: toInt(pick(message, 'MessageId', 'messageId', 'Id'), 0),
    Kind: toInt(pick(message, 'Kind', 'kind'), MESSAGE_KIND.Comment),
    UserId: toInt(pick(message, 'UserId', 'userId'), 0),
    UserName: toText(pick(message, 'UserName', 'userName')),
    Text: toText(pick(message, 'Text', 'text')),
    OldAnswer: toText(pick(message, 'OldAnswer', 'oldAnswer')),
    NewAnswer: toText(pick(message, 'NewAnswer', 'newAnswer')),
    CreatedOnUtc: toText(pick(message, 'CreatedOnUtc', 'createdOnUtc')),
    Files: (pick(message, 'Files', 'files') || []).map(normalizeFile),
})

export const normalizeThread = thread => {
    if (!thread || typeof thread !== 'object') return null
    const messages = pick(thread, 'Messages', 'messages')
    return {
        ThreadId: toInt(pick(thread, 'ThreadId', 'threadId', 'Id'), 0),
        AuditQuestionId: toInt(pick(thread, 'AuditQuestionId', 'auditQuestionId'), 0),
        Status: toInt(pick(thread, 'Status', 'status'), THREAD_STATUS.Open),
        Round: toInt(pick(thread, 'Round', 'round'), 1),
        OpenedByUserId: toInt(pick(thread, 'OpenedByUserId', 'openedByUserId'), 0),
        OpenedByName: toText(pick(thread, 'OpenedByName', 'openedByName')),
        OpenedOnUtc: toText(pick(thread, 'OpenedOnUtc', 'openedOnUtc')),
        LastActivityOnUtc: toText(pick(thread, 'LastActivityOnUtc', 'lastActivityOnUtc')),
        ClosedByName: toText(pick(thread, 'ClosedByName', 'closedByName')),
        ClosedOnUtc: toText(pick(thread, 'ClosedOnUtc', 'closedOnUtc')),
        CurrentAnswer: stripHtml(pick(thread, 'CurrentAnswer', 'currentAnswer')),
        CanReply: toBool(pick(thread, 'CanReply', 'canReply')),
        CanClose: toBool(pick(thread, 'CanClose', 'canClose')),
        CanReopen: toBool(pick(thread, 'CanReopen', 'canReopen')),
        CanComment: toBool(pick(thread, 'CanComment', 'canComment')),
        Messages: Array.isArray(messages) ? messages.map(normalizeMessage) : [],
    }
}

export const countThreads = threads => {
    const counts = { Open: 0, Replied: 0, Closed: 0 }
    const list = threads || []
    list.forEach(t => {
        if (!t) return
        if (t.Status === THREAD_STATUS.Open) counts.Open += 1
        else if (t.Status === THREAD_STATUS.Replied) counts.Replied += 1
        else if (t.Status === THREAD_STATUS.Closed) counts.Closed += 1
    })
    return counts
}

export const threadMapByQuestion = threads =>
    (threads || []).reduce((acc, t) => {
        if (t && t.AuditQuestionId > 0) acc[String(t.AuditQuestionId)] = t
        return acc
    }, {})

/** GET Inspection/{auditId}/Threads -> screen state. Anything but ReviewFlow 1 / 2 = not available (show nothing). */
/** "Review done" step: { Required, Done, DoneByUserId, DoneByName, DoneOnUtc, Note, CanMarkDone, CanUndo }. */
export const normalizeReview = review => ({
    Required: toBool(pick(review, 'Required', 'required')),
    Done: toBool(pick(review, 'Done', 'done')),
    DoneByUserId: toInt(pick(review, 'DoneByUserId', 'doneByUserId'), 0),
    DoneByName: toText(pick(review, 'DoneByName', 'doneByName')),
    DoneOnUtc: toText(pick(review, 'DoneOnUtc', 'doneOnUtc')),
    Note: toText(pick(review, 'Note', 'note')),
    CanMarkDone: toBool(pick(review, 'CanMarkDone', 'canMarkDone')),
    CanUndo: toBool(pick(review, 'CanUndo', 'canUndo')),
    ApprovalWaitsForComments: toBool(pick(review, 'ApprovalWaitsForComments', 'approvalWaitsForComments')),
})

export const MAX_REVIEW_NOTE = 2000

/** Error text for the review note, '' when fine. */
export const validateReviewNote = note =>
    toText(note).trim().length > MAX_REVIEW_NOTE ? `Keep the note under ${MAX_REVIEW_NOTE} characters.` : ''

/** Warning in the "Mark review done" dialog; the approval part only when the approval really waits for comments. */
export const openCommentsWarning = (count, approvalWaits) => {
    const n = toInt(count, 0)
    if (n <= 0) return ''
    const head = n === 1 ? '1 comment is still open' : `${n} comments are still open`
    if (!approvalWaits) return `${head}.`
    return `${head} - the approval also waits for ${n === 1 ? 'it' : 'them'}.`
}

/** Comments the approval still waits for (Open + Replied). */
export const openCommentCount = counts => toInt(counts && counts.Open, 0) + toInt(counts && counts.Replied, 0)

/** Text of the "review done" status line. */
export const reviewStatusText = review => {
    if (!review || !review.Required) return ''
    if (!review.Done) return 'Review: not done'
    const by = review.DoneByName ? ` by ${review.DoneByName}` : ''
    const when = formatThreadTime(review.DoneOnUtc)
    return `Review done${by}${when ? ` on ${when}` : ''}`
}

export const buildReviewDonePayload = ({ note } = {}) => {
    const trimmed = toText(note).trim()
    return trimmed ? { Note: trimmed } : {}
}

export const normalizeThreadsResponse = response => {
    const reviewFlow = toInt(pick(response, 'ReviewFlow', 'reviewFlow'), 0)
    const rawThreads = pick(response, 'Threads', 'threads')
    const threads = (Array.isArray(rawThreads) ? rawThreads : []).map(normalizeThread).filter(Boolean)
    return {
        available: !!response && pick(response, 'Success', 'success') !== false && isReviewFlowOn(reviewFlow),
        reviewFlow,
        answerChangeAllowed: toBool(pick(response, 'AnswerChangeAllowed', 'answerChangeAllowed')),
        canComment: toBool(pick(response, 'CanComment', 'canComment')),
        canReply: toBool(pick(response, 'CanReply', 'canReply')),
        review: normalizeReview(pick(response, 'Review', 'review')),
        threads,
    }
}

/** Replace the thread with the same id (or the same question) in the list, or add it. */
export const upsertThread = (threads, thread) => {
    const next = normalizeThread(thread)
    if (!next) return threads || []
    const list = threads || []
    const index = list.findIndex(
        t => (next.ThreadId > 0 && t.ThreadId === next.ThreadId) || t.AuditQuestionId === next.AuditQuestionId,
    )
    if (index < 0) return [...list, next]
    return list.map((t, i) => (i === index ? next : t))
}

const nestedIds = nestedItems => (nestedItems || []).map(n => n && n.Id).filter(id => id != null)

/** The question (or one of its nested child questions) has a thread. */
export const questionHasThread = (item, nestedItems, map) => {
    if (!item || !map) return false
    if (map[String(item.Id)]) return true
    return nestedIds(nestedItems).some(id => !!map[String(id)])
}

/** "Show only questions with comments": hide a question card without a thread (its nested children counted). */
export const shouldHideQuestion = ({ filterOn, item, nestedItems, map }) =>
    !!filterOn && !questionHasThread(item, nestedItems, map)

export const answerChangeText = (oldAnswer, newAnswer) =>
    `Answer changed: ${stripHtml(oldAnswer) || '(blank)'} -> ${stripHtml(newAnswer) || '(blank)'}`

const answerChangeCount = thread =>
    ((thread && thread.Messages) || []).filter(m => m && toInt(m.Kind) === MESSAGE_KIND.AnswerChanged).length

/** The saved thread has more "answer changed" messages than before (the reply really changed the answer). */
export const gainedAnswerChange = (before, after) => {
    const next = normalizeThread(after)
    if (!next) return false
    return answerChangeCount(next) > answerChangeCount(before)
}

/** Headline of a message in the thread. */
export const messageHeadline = message => {
    const who = (message && message.UserName) || 'Someone'
    switch (message && message.Kind) {
        case MESSAGE_KIND.Reply:
            return `${who} replied`
        case MESSAGE_KIND.AnswerChanged:
            return answerChangeText(message.OldAnswer, message.NewAnswer)
        case MESSAGE_KIND.Closed:
            return `${who} closed the comment`
        case MESSAGE_KIND.Reopened:
            return `${who} reopened the comment`
        default:
            return `${who} commented`
    }
}

/** Uploaded file (Inspection/Media/Upload result + the picked file) -> { FileKey, FileName, FileType }. */
export const toFilePayload = file => ({
    FileKey: toText(pick(file, 'FileKey', 'CloudImageId')),
    FileName: toText(pick(file, 'FileName', 'name')).slice(0, 200),
    FileType: toText(pick(file, 'FileType', 'type')) || 'application/octet-stream',
})

const filesPayload = files =>
    (files || [])
        .map(toFilePayload)
        .filter(f => f.FileKey)
        .slice(0, MAX_THREAD_FILES)

/** Open a thread / add a reviewer comment / reopen: { Text, Files }. */
export const buildCommentPayload = ({ text, files } = {}) => ({
    Text: toText(text).trim(),
    Files: filesPayload(files),
})

export const buildClosePayload = ({ text } = {}) => {
    const trimmed = toText(text).trim()
    return trimmed ? { Text: trimmed } : {}
}

/** Reply: { Text, Files, AnswerChange? }; AnswerChange = { OptionIds } for choice questions, { Value } for others. */
export const buildReplyPayload = ({ text, files, answerChange } = {}) => {
    const payload = buildCommentPayload({ text, files })
    if (answerChange) {
        if (Array.isArray(answerChange.optionIds)) {
            payload.AnswerChange = {
                OptionIds: answerChange.optionIds.map(id => toInt(id, 0)).filter(id => id > 0),
            }
        } else if (answerChange.value !== undefined) {
            payload.AnswerChange = { Value: toText(answerChange.value) }
        }
    }
    return payload
}

/**
 * Error text for a message form, '' when fine. mode: 'comment' / 'reopen' need text; 'reply' needs text, a file or an
 * answer change.
 */
export const validateMessage = ({ mode, text, files, answerChange } = {}) => {
    const trimmed = toText(text).trim()
    const fileCount = (files || []).length
    if (trimmed.length > MAX_THREAD_TEXT) return `Keep the text under ${MAX_THREAD_TEXT} characters.`
    if (fileCount > MAX_THREAD_FILES) return `Attach at most ${MAX_THREAD_FILES} files.`
    if (mode === 'reply') {
        if (answerChange && Array.isArray(answerChange.optionIds) && !answerChange.optionIds.length) {
            return 'Pick the new answer.'
        }
        if (!trimmed && !fileCount && !answerChange) return 'Write a reply or attach a file.'
        return ''
    }
    if (!trimmed) return 'Write a comment.'
    return ''
}

// Answer change while replying: choice questions (TypeOfResponse 2) pick options, text / number / long text type a value.
const SINGLE_VALUE_TYPES = { 1: 'text', 2: 'number', 13: 'text' }
const PLAIN_NUMBER = /^(-?\d+(\.\d+)?)?$/

/** 'options' | 'options-multi' | 'text' | 'number' | null (no answer change offered for that question type). */
export const answerChangeKind = item => {
    if (!item) return null
    // customer picker (18) and parent / child answers are not changed from a reply
    if (toInt(item.QuestionResponseId) === 18) return null
    const parentChild = item.Answers && item.Answers.ParentChildResponse
    if (Array.isArray(parentChild) && parentChild.length) return null
    if (toInt(item.TypeOfResponse) === 2) {
        const options = (item.Answers && item.Answers.MultipleResponse) || []
        if (!options.length) return null
        return toInt(item.MultiResponseType) === 2 ? 'options-multi' : 'options'
    }
    if (toInt(item.TypeOfResponse) === 1) {
        const kind = SINGLE_VALUE_TYPES[toInt(item.QuestionResponseId)] || null
        // Number answers may hold inch notation (33 1/2"): keep a text box unless the stored value is a plain number
        if (kind === 'number' && !PLAIN_NUMBER.test(currentValue(item).trim())) return 'text'
        return kind
    }
    return null
}

export const answerOptions = item =>
    ((item && item.Answers && item.Answers.MultipleResponse) || [])
        .filter(o => o && o.Id != null)
        .map(o => ({ key: String(o.Id), value: toInt(o.Id), text: stripHtml(o.DisplayText) || `Option ${o.Id}` }))

export const currentOptionIds = item =>
    ((item && item.Answers && item.Answers.MultipleResponse) || []).filter(o => o && o.IsSelected).map(o => toInt(o.Id))

export const currentValue = item => toText(item && item.Answers && item.Answers.SingleResponse && item.Answers.SingleResponse.Response)

// Template setting "Review flow"
const userRows = rows =>
    (Array.isArray(rows) ? rows : [])
        .map(c => ({ UserId: toInt(pick(c, 'UserId', 'userId'), 0), Name: toText(pick(c, 'Name', 'name')) }))
        .filter(c => c.UserId > 0)

export const normalizeReviewSettings = response => {
    const list = userRows(pick(response, 'Commenters', 'commenters'))
    const available = pick(response, 'AvailableReviewers', 'availableReviewers')
    const flow = toInt(pick(response, 'ReviewFlow', 'reviewFlow'), 0)
    return {
        ReviewFlow: [0, 1, 2].includes(flow) ? flow : 0,
        AnswerChangeAllowed: toBool(pick(response, 'AnswerChangeAllowed', 'answerChangeAllowed')),
        ApprovalWaitsForComments: toBool(pick(response, 'ApprovalWaitsForComments', 'approvalWaitsForComments')),
        ReviewDoneRequired: toBool(pick(response, 'ReviewDoneRequired', 'reviewDoneRequired')),
        Commenters: list,
        // the server says whether the caller may save (absent = allowed; the POST still checks)
        CanEdit: pick(response, 'CanEdit', 'canEdit') === undefined ? true : toBool(pick(response, 'CanEdit', 'canEdit')),
        // absent (older backend) = the list cannot be changed here, so it is never sent
        CanEditCommenters: toBool(pick(response, 'CanEditCommenters', 'canEditCommenters')),
        // active company users the caller may pick (only sent with CanEditCommenters); null = not in the response
        AvailableReviewers: Array.isArray(available) ? userRows(available) : null,
    }
}

const uniqueIds = list => {
    const ids = []
    const source = list || []
    source.forEach(id => {
        const n = toInt(id, 0)
        if (n > 0 && !ids.includes(n)) ids.push(n)
    })
    return ids
}

/** Same set of commenters (order ignored). */
export const sameCommenters = (a, b) => {
    const x = uniqueIds(a).sort((p, q) => p - q)
    const y = uniqueIds(b).sort((p, q) => p - q)
    return x.length === y.length && x.every((v, i) => v === y[i])
}

/**
 * POST body. CommenterUserIds is sent only when the caller may change the list (CanEditCommenters) AND it changed;
 * otherwise it is left out and the server keeps the stored list.
 */
/**
 * "Who can comment" options: the server's AvailableReviewers, plus saved reviewers that are not in it, by their saved
 * name (never dropped; the server refuses inactive ones with 400 INVALID_COMMENTERS).
 */
export const reviewerOptions = (availableReviewers, savedCommenters) => {
    const options = userRows(availableReviewers).map(u => ({ key: String(u.UserId), value: u.UserId, text: u.Name || `User ${u.UserId}` }))
    userRows(savedCommenters).forEach(c => {
        if (!options.some(o => o.value === c.UserId)) {
            options.push({ key: String(c.UserId), value: c.UserId, text: c.Name || `User ${c.UserId}` })
        }
    })
    return options
}

export const buildReviewSettingsPayload = (
    { ReviewFlow, AnswerChangeAllowed, ApprovalWaitsForComments, ReviewDoneRequired, CommenterUserIds } = {},
    { canEditCommenters = false, savedCommenterIds = [] } = {},
) => {
    const flow = toInt(ReviewFlow, 0)
    const payload = {
        ReviewFlow: [0, 1, 2].includes(flow) ? flow : 0,
        AnswerChangeAllowed: !!AnswerChangeAllowed,
        ApprovalWaitsForComments: !!ApprovalWaitsForComments,
        ReviewDoneRequired: !!ReviewDoneRequired,
    }
    if (canEditCommenters && !sameCommenters(CommenterUserIds, savedCommenterIds)) {
        payload.CommenterUserIds = uniqueIds(CommenterUserIds)
    }
    return payload
}

// Inbox: GET Threads/WaitingOnMe
const waitingForKey = value => {
    const key = toText(value).trim().toLowerCase()
    return key === 'review' || key === 'finish-review' ? key : 'reply'
}

export const normalizeWaitingItems = response => {
    const items = pick(response, 'Items', 'items')
    return (Array.isArray(items) ? items : [])
        .map(i => ({
            ThreadId: toInt(pick(i, 'ThreadId', 'threadId'), 0),
            AuditId: toInt(pick(i, 'AuditId', 'auditId'), 0),
            AuditGuid: toText(pick(i, 'AuditGuid', 'auditGuid')),
            AuditName: toText(pick(i, 'AuditName', 'auditName')),
            TemplateName: toText(pick(i, 'TemplateName', 'templateName')),
            AuditQuestionId: toInt(pick(i, 'AuditQuestionId', 'auditQuestionId'), 0),
            QuestionText: stripHtml(pick(i, 'QuestionText', 'questionText')),
            Status: toInt(pick(i, 'Status', 'status'), THREAD_STATUS.Open),
            Round: toInt(pick(i, 'Round', 'round'), 1),
            LastActivityOnUtc: toText(pick(i, 'LastActivityOnUtc', 'lastActivityOnUtc')),
            WaitingSinceUtc: toText(pick(i, 'WaitingSinceUtc', 'waitingSinceUtc')),
            WaitingFor: waitingForKey(pick(i, 'WaitingFor', 'waitingFor')),
        }))
        // "finish-review" rows are a whole inspection (ThreadId 0, no question)
        .filter(i => (i.ThreadId > 0 || i.WaitingFor === 'finish-review') && (i.AuditGuid || i.AuditId > 0))
}

const WAITING_TEXT = { reply: 'Your reply', review: 'Your review', 'finish-review': 'Finish review' }

export const waitingForText = waitingFor => WAITING_TEXT[waitingFor] || WAITING_TEXT.reply

/** Web route of the inspection (GUID preferred, like the other screens). */
export const inspectionRouteFor = item => {
    const id = (item && (item.AuditGuid || (item.AuditId > 0 ? item.AuditId : ''))) || ''
    return id ? `/app/templates/inspection/${id}` : ''
}

export const threadAnchorId = auditQuestionId => `qthread-q-${auditQuestionId}`

/** 503 (not installed) / 404 (route missing on an older backend): the feature is simply not there - show nothing. */
export const isThreadsUnavailable = error => {
    const status = error && error.status
    return status === 503 || status === 404
}

/**
 * Toast text for a failed call; '' when the shared API layer already showed one (role / audit-access refusals).
 * 403 NOT_ALLOWED gets a fixed sentence.
 */
export const threadErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
    if (!error) return fallback
    if (error.roleRefusalHandled || error.auditAccessHandled) return ''
    const body = error.error && typeof error.error === 'object' ? error.error : {}
    const code = body.ErrorCode || body.errorCode || body.code
    const message = body.Message || body.message || (typeof error.error === 'string' ? error.error : '')
    if (error.status === 503 && code === 'QUESTION_COMMENTS_NOT_INSTALLED') {
        return 'Question comments are not available yet.'
    }
    // 403: the server's reason when it sends one, else a fixed sentence
    if (error.status === 403) return message || NOT_ALLOWED_MESSAGE
    return message || error.message || fallback
}

/** Load error of the Review flow page: 404 NOT_FOUND = this template; 503 / bare 404 = feature not on the server. */
export const reviewSettingsLoadError = error => {
    const body = error && error.error && typeof error.error === 'object' ? error.error : {}
    const code = body.ErrorCode || body.errorCode || body.code
    if (error && error.status === 404 && code === 'NOT_FOUND') return "This template can't be configured here."
    if (error && (error.status === 503 || (error.status === 404 && !code))) {
        return 'Question comments are not available on this server yet.'
    }
    return threadErrorMessage(error, 'The review flow could not be loaded.') || 'The review flow could not be loaded.'
}
